import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  adminNotificationsApi,
  ADMIN_NOTIFICATIONS_QUERY_KEY,
  ADMIN_NOTIFICATIONS_UNREAD_QUERY_KEY,
} from '../../api/admin';
import { isOrchestratorDisabled } from '../../lib/adminSession';

export function AdminNotificationBell({ enabled }: { enabled: boolean }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);

  const live = enabled && !isOrchestratorDisabled();

  const unreadQ = useQuery({
    queryKey: ADMIN_NOTIFICATIONS_UNREAD_QUERY_KEY,
    queryFn: () => adminNotificationsApi.unreadCount(),
    enabled: live,
    refetchInterval: 15_000,
    retry: false,
  });

  const listQ = useQuery({
    queryKey: ADMIN_NOTIFICATIONS_QUERY_KEY,
    queryFn: () => adminNotificationsApi.list({ limit: 20 }),
    enabled: live && open,
    retry: false,
  });

  const invalidate = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ADMIN_NOTIFICATIONS_QUERY_KEY }),
      qc.invalidateQueries({ queryKey: ADMIN_NOTIFICATIONS_UNREAD_QUERY_KEY }),
    ]);
  };

  const markRead = useMutation({
    mutationFn: (id: string) => adminNotificationsApi.markRead(id),
    onSuccess: () => invalidate(),
  });

  const markAll = useMutation({
    mutationFn: () => adminNotificationsApi.markAllRead(),
    onSuccess: () => invalidate(),
  });

  if (!live) return null;

  const unread = unreadQ.data ?? 0;
  const items = listQ.data ?? [];

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          size="small"
          aria-label={unread > 0 ? `${unread} unread notifications` : 'Notifications'}
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{ flexShrink: 0 }}
        >
          <Badge
            color="primary"
            badgeContent={unread || undefined}
            overlap="circular"
            sx={{ '& .MuiBadge-badge': { fontWeight: 800, fontSize: '0.65rem' } }}
          >
            <NotificationsNoneRoundedIcon fontSize="small" />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 360, maxWidth: 'calc(100vw - 24px)' } } }}
      >
        <Stack direction="row" alignItems="center" sx={{ px: 1.5, py: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, flex: 1 }}>
            Notifications
          </Typography>
          <Button
            size="small"
            disabled={unread === 0 || markAll.isPending}
            onClick={() => markAll.mutate()}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Mark all read
          </Button>
        </Stack>
        <Divider />
        {items.length === 0 ? (
          <Box sx={{ px: 2, py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No notifications yet.
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding sx={{ maxHeight: 420, overflow: 'auto' }}>
            {items.map((item) => {
              const unreadItem = !item.readAt;
              return (
                <ListItemButton
                  key={item.id}
                  alignItems="flex-start"
                  onClick={() => {
                    if (unreadItem) markRead.mutate(item.id);
                    setAnchor(null);
                    const link = item.deepLink?.trim();
                    if (link) void navigate(link);
                    else if (item.applicationId) {
                      void navigate(`/applications/${encodeURIComponent(item.applicationId)}`);
                    }
                  }}
                  sx={{
                    bgcolor: unreadItem ? 'action.hover' : undefined,
                    borderLeft: unreadItem ? 3 : 0,
                    borderColor: 'primary.main',
                  }}
                >
                  <ListItemText
                    primary={item.title}
                    secondary={item.body}
                    primaryTypographyProps={{ fontWeight: unreadItem ? 800 : 600, fontSize: '0.85rem' }}
                    secondaryTypographyProps={{ fontSize: '0.75rem' }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Popover>
    </>
  );
}
